
import { defineComponent, computed, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { ElForm } from 'element-plus'
import { useStore, Store } from '@/store'

import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import AddressByDepartment from '@/components/layout/AddressByDepartment.vue'
import AddressSelectDialog from '@/components/dialogs/AddressSelectDialog.vue'
import TemplateSelectDialog from '@/components/dialogs/TemplateSelectDialog.vue'
import MessageAttacheFiles from '@/components/inputs/MessageAttacheFiles.vue'
import MessageSend from '@/components/actions/MessageSend.vue'
import ReceiveActions from '@/components/actions/ReceiveActions.vue'
import PushSenderQuestion from '@/components/actions/PushSenderQuestion.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import BoundaryInfo from '@/components/layout/BoundaryInfo.vue'
import FormItemDisabled from '@/components/layout/FormItemDisabled.vue'
import { MessageTypes } from '@/constants/messageTypes'

import { MessageForm } from '@/types/forms'
import { RequestTypes, MessageActions } from '@/constants/enums'
import { MessageAddress, RequestHeaderDetail, Message } from '@/api'
import MessageTypeLogic from '@/services/messageTypeLogic'
import { messageRule } from '@/constants/rules'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import { ReadRequest as MessageReadRequest } from '@/requests/message'
import { addMessageQueue, MESSAGE_TYPE_ERROR, MESSAGE_TYPE_TOAST } from '@/plugins/messageQueue'
import MessageService from '@/services/message'
import { getSenderFromMessage, isEmptyApplicantMailAddress } from '@/services/messageAddress'
import Logger from '@/services/logger'

/**
 * URLが正しいかチェックする
 */
const checkUrl = (
  requestHeaderId: number | null,
  messageType: number | null,
  action: string,
  messageId: null | number
) => {
  if (requestHeaderId == null) {
    Logger.info('申請基本IDが不正です')
    return false
  }
  if (messageType == null) {
    Logger.info('メッセージ種別が不正です')
    return false
  }
  const logic = new MessageTypeLogic(messageType, requestHeaderId)
  // action毎のチェック
  if (action === MessageActions.SEND.key) {
    // 送信
    if ((!logic.isNormalType() && !logic.isTransceiveType()) || !logic.isJrSendType()) {
      // 正しい種別では無いか、通常メッセージ送信画面で対応できないものであれば404へ飛ばす
      Logger.info(`メッセージ種別が不正です。種別: ${messageType}`)
      return false
    } else {
      return true
    }
  } else if (action === MessageActions.REPLY.key) {
    if ((!logic.isNormalType() && !logic.isTransceiveType()) || !logic.isJrSendType()) {
      // 正しい種別では無いか、通常メッセージ送信画面で対応できないものであれば404へ飛ばす
      Logger.info(`メッセージ種別が不正です。種別: ${messageType}`)
      return false
    } else if (messageId == null) {
      Logger.info('返信先IDがありません。')
      return false
    } else {
      return true
    }
  } else if (action === MessageActions.RECEIVE.key) {
    if (!logic.isNormalType() && !logic.isTransceiveType()) {
      // 正しい種別では無い場合404へ飛ばす
      Logger.info(`メッセージ種別が不正です。種別: ${messageType}`)
      return false
    } else if (messageId == null) {
      // メッセージIDチェック
      Logger.info('メッセージIDがありません。')
      return false
    } else {
      return true
    }
  } else {
    Logger.info(`actionが不正です。action: ${action}`)
    return false
  }
}

/**
 * 共通メッセージ画面
 */
export default defineComponent({
  name: 'Message',
  components: {
    InputGroupDark,
    DoubleScroll,
    UserLoginWrapper,
    ProximityInfo,
    InputLabel,
    AddressByDepartment,
    AddressSelectDialog,
    TemplateSelectDialog,
    JrTextarea,
    MessageAttacheFiles,
    MessageSend,
    ReceiveActions,
    PushSenderQuestion,
    JrApplicantTitle,
    BoundaryInfo,
    FormItemDisabled,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore() as Store

    const formRef = ref<InstanceType<typeof ElForm>>()
    const attacheFileRef = ref<InstanceType<typeof MessageAttacheFiles>>()
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    const requestType = ref<number | null | undefined>(null)
    const requestHeader = ref<RequestHeaderDetail | null>()
    // メッセージID
    const messageId = computed(() => {
      if (!('message_id' in route.params)) {
        // メッセージIDが無い場合(送信画面)はnull
        return null
      }
      const id = Number(route.params.message_id)
      if (Number.isNaN(id)) {
        // メッセージIDが数値では無い場合null
        return null
      }
      return id
    })

    // 添付ファイルId
    const attachedFileId = ref<number | null>(null)
    // 種別
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    // 種別ロジック
    const typeLogic = computed(() => {
      return messageType.value != null && requestHeaderId.value != null
        ? new MessageTypeLogic(messageType.value, requestHeaderId.value)
        : null
    })
    // 種別名
    const typeName = computed(() => {
      return typeLogic.value != null ? typeLogic.value.getTypeName() : ''
    })
    // 添付ファイル可能か
    const canAttached = computed(() => {
      return typeLogic.value != null ? typeLogic.value.canAttachedFile() : false
    })
    // 添付ファイルが必須か
    const isRequiredAttached = computed(() => {
      return typeLogic.value != null ? typeLogic.value.isRequiredAttached() : false
    })
    // 備考設定可能か
    const canRemark = computed(() => {
      return typeLogic.value != null ? typeLogic.value.canRemark() : false
    })
    // 編集可能か(actionがsendかreplyなら編集可)
    const isEdit = computed(() => {
      return route.params.action === MessageActions.SEND.key || route.params.action === MessageActions.REPLY.key
    })
    // 履歴テーブルを表示するか（recvの場合のみ表示）
    const isHistoryTableVisible = computed(() => {
      return route.params.action === MessageActions.RECEIVE.key
    })
    // 宛先ダイアログのopenが可能か
    const isAddressVisible = computed(() => {
      if (form.value == null) {
        return false
      }
      const isReceiverApplicant = form.value.message_address.some((address) => address.is_applicant)
      // 宛先に協議者が含まれない場合に表示できる
      return !isReceiverApplicant
    })
    const fixedDepartmentId = computed(() => {
      if (requestHeader.value == null) {
        return null
      }
      return typeLogic.value != null ? typeLogic.value.getFixedDepartmentId(requestHeader.value) : null
    })

    const action = computed(() => {
      return route.params.action
    })

    const maxAttachedFiles = computed(() => {
      return typeLogic.value?.getMaxAttachedFiles()
    })

    const form = ref<MessageForm | null>(null)
    // 送信者情報
    const senderAddress = computed(() => {
      if (isEdit.value) {
        // 編集時はログイン者の情報
        return store.getters['user/getSenderMessageAddress']
      } else {
        // 閲覧時はformの内容から送信者を返す
        return getSenderFromMessage(form.value as Message)
      }
    })

    /**
     * 詳細画面に戻る
     */
    const returnDetail = () => {
      if (requestType.value === RequestTypes.PROXIMITY && requestHeaderId.value != null) {
        router.push({
          name: 'ProximityDetail',
          params: {
            request_header_id: requestHeaderId.value,
          },
        })
      }
      if (requestType.value === RequestTypes.BOUNDARY && requestHeaderId.value != null) {
        router.push({
          name: 'BoundaryDetail',
          params: {
            request_header_id: requestHeaderId.value,
          },
        })
      }
    }

    /**
     * 各action毎の動作
     */
    const actionProcess = async () => {
      if (action.value === MessageActions.SEND.key && typeLogic.value != null) {
        // 送信時
        // 初期メッセージを取得
        const user = store.getters['user/getUser']
        if (user != null) {
          const message = await typeLogic.value.createConfiguredMessage(user, false)
          if (isEmptyApplicantMailAddress(message.message_address)) {
            // 申請者宛てなのに申請者メールアドレスが設定されていない場合エラー
            addMessageQueue(MESSAGE_TYPE_ERROR, MessageService.codes.NOT_SET_APPLICANT_EMAIL, () => returnDetail())
          } else {
            form.value = message as MessageForm
          }
        }
      } else if (action.value === MessageActions.REPLY.key && typeLogic.value != null) {
        // 返信
        // 初期メッセージを取得
        const user = store.getters['user/getUser']
        if (user != null) {
          const message = await typeLogic.value.createConfiguredMessage(user, false, messageId.value as number)
          if (isEmptyApplicantMailAddress(message.message_address)) {
            // 申請者宛てなのに申請者メールアドレスが設定されていない場合エラー
            addMessageQueue(MESSAGE_TYPE_ERROR, MessageService.codes.NOT_SET_APPLICANT_EMAIL, () => returnDetail())
          } else {
            form.value = message as MessageForm
          }
        }
      } else if (action.value === MessageActions.RECEIVE.key && typeLogic.value != null && messageId.value != null) {
        // 受信
        // メッセージ取得
        const request = new MessageReadRequest(messageId.value)
        if (await request.exec()) {
          const { data } = request.getResponse() as AxiosResponse<Message>
          // 種別チェック
          if (data.message_type !== messageType.value) {
            // URLのメッセージ種別とメッセージの種別が異なる
            Logger.info(
              `メッセージ種別が異なります。URLメッセージ種別: ${messageType.value} メッセージの種別: ${data.message_type}`
            )
            router.push({ name: 'NotFound' })
            return
          }
          // formに内容を格納
          form.value = data as MessageForm
          // 回答請書受領の場合 受信した添付ファイルのファイルIDを格納
          if (data.message_type === MessageTypes.P_AS_ANSWER_RECEIPT_NOTICE && form.value && form.value.attached_file) {
            if (form.value.attached_file[0].file) {
              attachedFileId.value = form.value.attached_file[0].file
            }
          }
        }
      }
      // form内容が反映後、validationをリセットする
      nextTick(() => {
        if (formRef.value) {
          formRef.value.clearValidate()
        }
      })
    }

    // 主処理

    watch(
      () => [action, requestHeaderId, messageType, messageId, typeLogic],
      async () => {
        if (route.name !== 'NormalMessageSend' && route.name !== 'NormalMessage') {
          // 遷移中(既に他画面となっている時)は何もしない
          return
        }
        // 主処理
        if (!checkUrl(requestHeaderId.value, messageType.value, action.value as string, messageId.value)) {
          // URLが不正な場合、404に飛ばす
          router.push({ name: 'NotFound' })
        } else {
          // 申請基本情報の取得
          const request = new RequestHeaderReadRequest(requestHeaderId.value as number)
          await request.exec()

          if (request.success) {
            const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
            requestType.value = data.request_type
            requestHeader.value = data
            // 申請基本が取得できたら各action毎の動作を実行
            actionProcess()
          }
        }
      },
      { immediate: true, deep: true }
    )

    /**
     * 宛先の更新
     */
    const updateMessageAddress = (ma: MessageAddress[]) => {
      if (form.value != null) {
        form.value.message_address.splice(0)
        form.value.message_address.push(...ma)
      }
    }

    /**
     * 送信後のページ遷移
     */
    const pushDetail = (sentResult: boolean) => {
      if (sentResult) {
        // 送信成功時、詳細画面に遷移させる
        addMessageQueue(
          MESSAGE_TYPE_TOAST,
          MessageService.bind(MessageService.codes.SEND_COMPLETE, { target: 'メッセージ' })
        )
        returnDetail()
      }
      // falseになるパターンは例外となるためここに来ない
    }

    /**
     * 必須添付バリデーションチェック
     * スロットコンポーネントのバリデーションが効かないため
     */
    watch(
      () => [form.value?.attached_file_required, form.value?.attached_file_selected_count],
      () => {
        validRequiredFile()
      },
      { deep: true }
    )
    const validRequiredFile = () => {
      if (form.value?.attached_file_required !== null && form.value?.attached_file_required !== 0) {
        formRef.value?.clearValidate('attached_file_required')
      }
      if (
        form.value?.attached_file_selected_count !== null &&
        (form.value?.attached_file_selected_count as number) > 0
      ) {
        formRef.value?.clearValidate('attached_file_selected_count')
      }
    }

    return {
      requestHeaderId,
      messageType,
      messageId,
      attachedFileId,
      typeName,
      canAttached,
      isRequiredAttached,
      canRemark,
      form,
      updateMessageAddress,
      rules: messageRule,
      RequestTypes,
      isEdit,
      isHistoryTableVisible,
      isAddressVisible,
      attacheFileRef,
      formRef,
      pushDetail,
      senderAddress,
      fixedDepartmentId,
      requestType,
      requestHeader,
      maxAttachedFiles,
    }
  },
})
